import * as React from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Stack from "@mui/material/Stack";
import { IconButton } from "@mui/material";
import { downloadChat } from "../../../../function/downloadChat";
import IChatbot from "../../../../interface/iChatbot";
import { AppContext } from "../../../../store/genesys.context";
import IMessage from "../../../../interface/IMessage";
import OptionIcon from "../../../atoms/icons/optionIcon.styled";
import type { Brand } from "../../../../lib/ChatbotConfig";

export default function OptionButton() {
  const {
    chatbot,
    brand,
    setChatInput,
    clearConversation,
  }: {
    chatbot: IChatbot;
    brand: Brand;
    setChatInput: React.Dispatch<React.SetStateAction<IMessage>>;
    clearConversation: () => void;
  } = React.useContext(AppContext);

  const [open, setOpen] = React.useState<boolean>(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  const handleFileUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".png, .jpg, .jpeg";
    input.onchange = (e) => {
      const selectedFile = (e.target as HTMLInputElement).files?.[0] ?? null;

      if (!selectedFile) {
        setOpen(false);
        return;
      }

      setChatInput(
        (prevState: IMessage): IMessage => ({
          ...prevState,
          content: selectedFile,
        }),
      );
      setOpen(false);
    };
    input.click();
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <Stack direction="row" spacing={2}>
      <div>
        <IconButton
          ref={anchorRef}
          id="composition-button"
          aria-label="Weitere Chat-Aktionen"
          aria-controls={open ? "composition-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          disableFocusRipple
          disableRipple
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "&:hover *": {
              color: (theme) =>
                theme.palette.widgetStyles.chatControls.iconHoverColor,
            },
            // A11Y FIX: visible keyboard focus indicator (MUI removes the
            // native outline and the ripple is disabled here).
            "&:focus-visible": {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "2px",
            },
          }}
        >
          <OptionIcon />
        </IconButton>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom-start" ? "left top" : "left bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    onKeyDown={handleListKeyDown}
                    // Reset list markers so global styles from the embedding
                    // host page (no shadow DOM) can't add bullets to the items.
                    sx={{
                      listStyle: "none",
                      "& .MuiMenuItem-root": {
                        listStyle: "none",
                        "&::before, &::marker": {
                          content: '""',
                          display: "none",
                        },
                      },
                    }}
                  >
                    <MenuItem onClick={handleFileUpload}>
                      Datei hochladen (.png, .jpg, .jpeg)
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        downloadChat(chatbot.messages, brand);
                        setOpen(false);
                      }}
                    >
                      Chat herunterladen
                    </MenuItem>

                    <MenuItem
                      onClick={() => {
                        clearConversation();
                        setOpen(false);
                      }}
                    >
                      Chatverlauf löschen
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </Stack>
  );
}
